<script setup lang="ts">
import { useRoute } from 'vue-router';

// Types
type LayoutType = 'login' | 'dashboard';

interface FooterLink {
    to: string;
    label: string;
}

// Props
const props = defineProps<{
    layout: LayoutType;
    contact?: string;
}>();

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// Route composable
const route = useRoute();

// Computed property to check if current page is a restaurant detail page
const isRestaurantPage = computed(() => {
     return /^\/restaurants\//.test(route.path);
});

// Constants
const footerLinks: FooterLink[] = [
    { to: '/', label: 'Home' },
    { to: '/contact', label: 'Contact Us' },
    { to: '/restaurant-owner', label: 'Restaurant Owner Registration' },
    { to: '/privacy', label: 'Privacy Policy' },
    { to: '/terms', label: 'Terms of Service' }
];

// Computed
const footerClass = computed(() => {
    const baseClasses = 'h-[11.6rem] py-[4rem] px-[1.8rem] border-[solitude] border-t-[.1rem] max-md:h-auto';

    const viewport = useViewport()
    return {
        [baseClasses + ' bg-white']: props.layout === 'login',
        [baseClasses]: props.layout === 'dashboard'
    };
});

const currentYear = computed(() => new Date().getFullYear());

const store = useRestaurantDetailStore();

// Function to get contact href
const getContactHref = () => {
    const restaurantData = store.restaurantData;
    return restaurantData && restaurantData.contact
        ? `tel:${restaurantData.contact}`
        : '#';
};
</script>

<template>
    <div v-if="isRestaurantPage"
        class="min-pc:hidden fixed bottom-0 border-t-[.1rem] border-[solitude] w-full bg-white py-[.8rem] px-[1.6rem] flex justify-center gap-[.8rem] z-[999999]">
        <!-- Restaurant only content goes here test -->
        <a :href="getContactHref()"
            class="rounded-[.8rem] bg-[goldenYellow] h-[3.4rem] flex items-center justify-center text-[1.2rem] font-bold w-[16.8rem]"
            :class="{ 'w-full': store?.restaurantData?.membership?.plan?.name === 'Basic' }">
            <CustomIcon class="mr-[0.6rem]" name="call" />
            Call Now
        </a>
        <button v-if="store?.restaurantData?.membership?.plan?.name != 'Basic'"
            class="rounded-[.8rem] bg-[goldenYellow] h-[3.4rem] flex items-center justify-center text-[1.2rem] font-bold w-[16.8rem]">
            Make a reservation
        </button>
    </div>
    <footer :class="footerClass">
        <div class="max-w-[116rem] mx-auto">
            <div class="flex gap-[4rem] items-center max-md:flex-col max-md:items-start max-md:gap-[2.4rem]"
                :class="{ 'max-md:pb-[3.2rem]': isRestaurantPage }">
                <!-- Logo -->
                <NuxtLink to="/" class="font-semibold leading-[1.5] text-[2.4rem] max-w-[10.2rem] block" @click="scrollToTop">
                    <img class="w-full" src="/img/common/ico_logo.png" alt="KICHIEATS Logo">
                </NuxtLink>

                <!-- Navigation Links -->
                <ul class="flex gap-[3.2rem] max-md:flex-col max-md:gap-[.8rem]">
                    <li v-for="link in footerLinks" :key="link.to" class="footer-item max-md:after:content-none" @click="scrollToTop">
                        <NuxtLink :to="link.to" class="text-[1.6rem] font-normal">
                            {{ link.label }}
                        </NuxtLink>
                    </li>
                </ul>

                <!-- Copyright -->
                <p class="text-[secondary] text-[1.4rem] font-normal">
                    Copyright © {{ currentYear }} KICHIEATS Inc.
                </p>
            </div>
        </div>
    </footer>
</template>
